import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  TextField,
  Tabs,
  Tab,
  Typography,
  List,
  CircularProgress,
  InputAdornment,
  Paper,
  Alert,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Button,
  useTheme
} from '@mui/material';
import { Search as SearchIcon, Star, CalendarToday } from '@mui/icons-material';
import { useApp } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';
import { searchUsers, searchPosts, getUserFollowing } from '../../services/api';
import UserCard from '../Profile/UserCard';
import PostCard from '../Feed/PostCard';
import MovieDetailsDialog from '../Movies/MovieDetailsDialog';
import { debounce } from 'lodash';
import axios from 'axios';
import { trackSearch, trackMovieView } from '../../utils/analytics';

const Search = () => {
  const { query: queryFromParams } = useParams();
  const navigate = useNavigate();
  const { addNotification } = useApp();
  const { user: currentUser } = useAuth();
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState(queryFromParams || '');
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [movies, setMovies] = useState([]);
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [movieDetails, setMovieDetails] = useState(null);
  const [currentUserFollowingList, setCurrentUserFollowingList] = useState([]);
  const [usersNextToken, setUsersNextToken] = useState(null);
  const [postsNextToken, setPostsNextToken] = useState(null);
  const [moviesPage, setMoviesPage] = useState(1);
  const [hasMoreMovies, setHasMoreMovies] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const searchMovies = async (query, page = 1) => {
    try {
      const response = await axios.get(
        `https://hype-circle-app-cloud-913961422896.europe-west1.run.app/api/movies/search/text?q=${encodeURIComponent(query)}&page=${page}&limit=20`
      );
      return {
        results: response.data.results || [],
        hasMore: response.data.results && response.data.results.length === 20
      };
    } catch (error) {
      console.error('Error searching movies:', error);
      return { results: [], hasMore: false };
    }
  };

  const debouncedSearch = useCallback(
    debounce(async (value) => {
      if (!value || value.trim() === '') {
        setUsers([]);
        setPosts([]);
        setMovies([]);
        setUsersNextToken(null);
        setPostsNextToken(null);
        setMoviesPage(1);
        setHasMoreMovies(true);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        // Fetch users, posts, and movies in parallel
        const [usersData, postsData, moviesData] = await Promise.all([
          searchUsers(value, 20),
          searchPosts(value, 20),
          searchMovies(value, 1)
        ]);

        setUsers(usersData?.items || []);
        setUsersNextToken(usersData?.nextToken || null);
        setPosts(postsData?.items || []);
        setPostsNextToken(postsData?.nextToken || null);
        setMovies(moviesData?.results || []);
        setMoviesPage(1);
        setHasMoreMovies(moviesData?.hasMore || false);
        setError(null);
        
        // Track search with total results
        const totalResults = (usersData?.items?.length || 0) + (postsData?.items?.length || 0) + (moviesData?.results?.length || 0);
        trackSearch(value, totalResults);
      } catch (err) {
        console.error("Error performing search:", err);
        setUsers([]);
        setPosts([]);
        setMovies([]);
        setUsersNextToken(null);
        setPostsNextToken(null);
        setMoviesPage(1);
        setHasMoreMovies(true);
        setError("Failed to fetch search results.");
        addNotification({ type: 'error', message: 'Could not perform search.' });
      } finally {
        setLoading(false);
      }
    }, 500),
    []
  );

  const handleUserFollowToggle = useCallback((userId, newFollowingStatus) => {
    // Update current user's following list
    setCurrentUserFollowingList(prev => {
      if (newFollowingStatus) {
        return prev.includes(userId) ? prev : [...prev, userId];
      } else {
        return prev.filter(id => id !== userId);
      }
    });

    // Update user's follower count in search results
    setUsers(prevUsers =>
      prevUsers.map(user => {
        if (user.id === userId) {
          return {
            ...user,
            followersCount: newFollowingStatus 
              ? (user.followersCount || 0) + 1 
              : Math.max((user.followersCount || 0) - 1, 0)
          };
        }
        return user;
      })
    );
  }, []);

  useEffect(() => {
    if (queryFromParams) {
      setSearchTerm(queryFromParams);
      debouncedSearch(queryFromParams);
    }
  }, [queryFromParams, debouncedSearch]);

  useEffect(() => {
    const loadCurrentUserFollowing = async () => {
      if (currentUser?.id) {
        try {
          const following = await getUserFollowing(currentUser.id);
          const followingIds = following.map(f => f.followee?.id).filter(Boolean);
          setCurrentUserFollowingList(followingIds);
        } catch (error) {
          console.error('Error loading following list:', error);
        }
      }
    };
    loadCurrentUserFollowing();
  }, [currentUser?.id]);

  const handleSearchChange = (event) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);
    debouncedSearch(newSearchTerm);
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search/${searchTerm.trim()}`);
      debouncedSearch.cancel(); // Cancel any pending debounced calls
      debouncedSearch(searchTerm.trim()); // And fire a new one immediately
    }
  };

  const handlePostUpdate = (postId, updates) => {
    setPosts(prevPosts =>
      prevPosts.map(post =>
        post.id === postId ? { ...post, ...updates } : post
      )
    );
  };

  const loadMoreUsers = async () => {
    if (!usersNextToken || loadingMore || !searchTerm) return;
    setLoadingMore(true);
    try {
      const moreUsers = await searchUsers(searchTerm, 20, usersNextToken);
      setUsers(prev => [...prev, ...(moreUsers?.items || [])]);
      setUsersNextToken(moreUsers?.nextToken || null);
    } catch (error) {
      console.error('Error loading more users:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const loadMorePosts = async () => {
    if (!postsNextToken || loadingMore || !searchTerm) return;
    setLoadingMore(true);
    try {
      const morePosts = await searchPosts(searchTerm, 20, postsNextToken);
      setPosts(prev => [...prev, ...(morePosts?.items || [])]);
      setPostsNextToken(morePosts?.nextToken || null);
    } catch (error) {
      console.error('Error loading more posts:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const loadMoreMovies = async () => {
    if (!hasMoreMovies || loadingMore || !searchTerm) return;
    setLoadingMore(true);
    try {
      const nextPage = moviesPage + 1;
      const moreMovies = await searchMovies(searchTerm, nextPage);
      setMovies(prev => [...prev, ...(moreMovies?.results || [])]);
      setMoviesPage(nextPage);
      setHasMoreMovies(moreMovies?.hasMore || false);
    } catch (error) {
      console.error('Error loading more movies:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleMovieClick = async (movie) => {
    setSelectedMovie(movie);
    setMovieDetails(null);
    
    // Track movie view from search
    trackMovieView(movie.id, movie.name);
    
    try {
      const response = await axios.get(
        `https://hype-circle-app-cloud-913961422896.europe-west1.run.app/api/movies/${movie.id}`
      );
      setMovieDetails(response.data);
    } catch (error) {
      console.error('Error fetching movie details:', error);
      setMovieDetails(movie);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
        Search
      </Typography>
      <Paper
        component="form"
        onSubmit={handleFormSubmit}
        sx={{ 
          p: 2, 
          mb: 4, 
          display: 'flex', 
          alignItems: 'center',
          background: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(15px)',
          border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search for users, posts, or movies..."
          value={searchTerm}
          onChange={handleSearchChange}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Paper sx={{ 
        mb: 3, 
        borderRadius: 2,
        background: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(15px)',
        border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          centered
        >
          <Tab label={`Users (${users.length})`} id="search-tab-0" aria-controls="search-tabpanel-0" />
          <Tab label={`Posts (${posts.length})`} id="search-tab-1" aria-controls="search-tabpanel-1" />
          <Tab label={`Movies (${movies.length})`} id="search-tab-2" aria-controls="search-tabpanel-2" />
        </Tabs>
      </Paper>

      {loading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && (
        <Box>
          <div role="tabpanel" hidden={activeTab !== 0} id="search-tabpanel-0" aria-labelledby="search-tab-0">
            <List>
              {users.length > 0 ? (
                <Box>
                  {users.map((user) => (
                    <UserCard 
                      key={user.id} 
                      user={user} 
                      variant="list" 
                      onFollowToggle={handleUserFollowToggle}
                      currentUserFollowingList={currentUserFollowingList}
                    />
                  ))}
                  {usersNextToken && (
                    <Box display="flex" justifyContent="center" mt={2}>
                      <Button 
                        variant="outlined" 
                        onClick={loadMoreUsers}
                        disabled={loadingMore}
                        sx={{
                          background: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                          backdropFilter: 'blur(15px)',
                          border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(255, 255, 255, 0.2)'
                        }}
                      >
                        {loadingMore ? <CircularProgress size={20} /> : 'Load More Users'}
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography textAlign="center" color="text.secondary" mt={4}>
                  No users found for "{searchTerm}".
                </Typography>
              )}
            </List>
          </div>

          <div role="tabpanel" hidden={activeTab !== 1} id="search-tabpanel-1" aria-labelledby="search-tab-1">
            <Box sx={{ pt: activeTab === 1 ? 2 : 0 }}>
              {posts.length > 0 ? (
                <Box>
                  {posts.map((post) => (
                    <Box key={post.id} mb={2}>
                      <PostCard post={post} onPostUpdate={handlePostUpdate} />
                    </Box>
                  ))}
                  {postsNextToken && (
                    <Box display="flex" justifyContent="center" mt={2}>
                      <Button 
                        variant="outlined" 
                        onClick={loadMorePosts}
                        disabled={loadingMore}
                        sx={{
                          background: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                          backdropFilter: 'blur(15px)',
                          border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(255, 255, 255, 0.2)'
                        }}
                      >
                        {loadingMore ? <CircularProgress size={20} /> : 'Load More Posts'}
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography textAlign="center" color="text.secondary" mt={4}>
                  No posts found for "{searchTerm}".
                </Typography>
              )}
            </Box>
          </div>

          <div role="tabpanel" hidden={activeTab !== 2} id="search-tabpanel-2" aria-labelledby="search-tab-2">
            <Box sx={{ pt: activeTab === 2 ? 2 : 0 }}>
              {movies.length > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    overflowX: 'auto',
                    pb: 2,
                    '&::-webkit-scrollbar': { height: 8 },
                    '&::-webkit-scrollbar-track': { bgcolor: 'grey.200' },
                    '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.400', borderRadius: 1 }
                  }}
                >
                  {movies.map((movie) => (
                    <Card
                      key={movie.id}
                      onClick={() => handleMovieClick(movie)}
                      sx={{
                        minWidth: 200,
                        maxWidth: 200,
                        cursor: 'pointer',
                        transition: 'transform 0.3s ease',
                        '&:hover': { transform: 'scale(1.05)' }
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="300"
                        image={movie.poster}
                        alt={movie.name}
                        sx={{ objectFit: 'cover' }}
                      />
                      <CardContent sx={{ p: 2 }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            minHeight: '2.5em',
                            mb: 1
                          }}
                        >
                          {movie.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                          {movie.rating && (
                            <Chip
                              label={movie.rating}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                bgcolor: 'primary.main',
                                color: 'white'
                              }}
                            />
                          )}
                          {movie.release_date && (
                            <Chip
                              label={new Date(movie.release_date).getFullYear()}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                bgcolor: 'grey.600',
                                color: 'white'
                              }}
                            />
                          )}
                        </Box>
                        {movie.genre && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: '0.7rem',
                              display: 'block',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {movie.genre}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                  {hasMoreMovies && (
                    <Box
                      sx={{
                        minWidth: 200,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 2
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={loadMoreMovies}
                        disabled={loadingMore}
                        sx={{
                          height: 'fit-content',
                          background: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                          backdropFilter: 'blur(15px)',
                          border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(255, 255, 255, 0.2)'
                        }}
                      >
                        {loadingMore ? <CircularProgress size={20} /> : 'Load More'}
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography textAlign="center" color="text.secondary" mt={4}>
                  No movies found for "{searchTerm}".
                </Typography>
              )}
            </Box>
          </div>
        </Box>
      )}

      {selectedMovie && (
        <MovieDetailsDialog
          open={true}
          onClose={() => {
            setSelectedMovie(null);
            setMovieDetails(null);
          }}
          movie={movieDetails || selectedMovie}
        />
      )}
    </Container>
  );
};

export default Search;
